import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, OnDestroy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Subscriber, Subscription } from 'rxjs';
import { EditorManager } from './prosemirror/editor-manager';
import { ContentChangedEvent } from './prosemirror/event/text-changed-event';
import { SelectEvent } from './prosemirror/event/select-event';
import { ShortcutEvent } from './prosemirror/event/shortcut-event';
import { MenuItem } from './menu/menu-item';
import { MenuEvent } from './prosemirror/event/menu-event';
import { MenuConfiguration } from './menu/menu-configuration';
import { MenuManager } from './menu/menu-manager';
import { MenuEnum } from './menu/menu-items.enum';
import { ToggleGroupItem } from './menu/toggle-group-item';
import { menuAlignConfiguration } from './menu/menu-align-confg';
import { MenuFontFamilyList } from './menu/menu-font-family-list';
import { MenuFontSizeList } from './menu/menu-font-size-list';
import { TextFormats, menuFontWeightConfiguration, menuFontStyleConfiguration, menuTextDecorationConfiguration, } from './menu/text-formats';
import { EditorContentConfiguration } from './prosemirror/configurations/editor-content';
import { EditorManagerConfiguration } from './prosemirror/configurations/editor-manager-configuration';
import { menuOrderedConfiguration, menuUnOrderedConfiguration, ListFormats } from './menu/menu-list';
import { TextAlignList } from './menu/text-align-list';
import { Suggestion } from '../se-smart-text-editor/suggestion-datasource/suggestion';
import { TranslateService } from '@ngx-translate/core';
import { TitleCasePipe } from '@angular/common';

@Component({
    selector: 'se-text-editor',
    templateUrl: './se-text-editor.component.html',
    styleUrls: ['./se-text-editor.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: SeTextEditorComponent,
        multi: true
    }]
})
export class SeTextEditorComponent implements OnInit, OnChanges, ControlValueAccessor, OnDestroy {
    @Input() suggestionList: Suggestion[] = [];
    @Input() customCommandConfiguration: MenuItem[];
    @Input() menuConfig: Map<MenuEnum, MenuConfiguration>;
    @Input() expandSuggestionButton;
    @Input() language = 'it';
    @Input() defaultFontSize = '14px';
    @Input() defaultFontFamily = 'arial';
    @Input() defaultTextAlign = 'left';
    @Input() defaultFontWeight = '';
    @Input() defaultFontStyle = 'normal';
    @Input() defaultTextDecoration = 'none';
    @Input() shortcutList;
    @Input() visibilityButtonEnabled = false;
    @Input('visible') isVisible?: boolean | null = true;
    @Input() title?: string;
    @Input() defaultTextName?: string = 'text';

    @Output() requestSuggestions = new EventEmitter<boolean>();
    @Output() contentChange = new EventEmitter<ContentChangedEvent>();
    @Output() selectionChange = new EventEmitter<SelectEvent>();
    @Output() shortcutDetected = new EventEmitter<ShortcutEvent>();
    @Output() commandRequested = new EventEmitter<MenuEvent>();
    @Output() visibilityNotify = new EventEmitter<boolean>();
    @Output() isEmpty = new EventEmitter<boolean>();
    @Output() startSpeechReconition = new EventEmitter<Array<string>>();

    public readOnlyContentDiv;
    public menuAlignConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuAlignConfiguration));
    public menuFontWeightConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuFontWeightConfiguration));
    public menuFontStyleConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuFontStyleConfiguration));
    public menuTextDecorationConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuTextDecorationConfiguration));
    public menuOrderedConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuOrderedConfiguration));
    public menuUnOrderedConfiguration: ToggleGroupItem[] = JSON.parse(JSON.stringify(menuUnOrderedConfiguration));
    public menuManager: MenuManager;
    public menuEnum = MenuEnum;
    public fontSizeList = MenuFontSizeList.list;
    public fontFamilyList = MenuFontFamilyList.list;
    public editorManager: EditorManager;
    private changeListenerFn = null;
    private touchListenerFn = null;
    public currentContentConfiguration: EditorContentConfiguration;
    public initialContentConfiguration: EditorContentConfiguration;
    private lastSelectedEvent: SelectEvent;
    private domEditorNode;
    private ignoreNextContentChange = false;
    public listConfiguration = { unordered: [''], ordered: [''] };
    public isDisabled = false;

    private speechServiceSubscription: Subscription;

    get isInactive() {
        return !this.isVisible || this.isDisabled;
    }

    constructor(
        private elementRef: ElementRef,
        private translate: TranslateService,
        private titleCasePipe: TitleCasePipe
    ) {}

    ngOnInit() {

        const textEditorConfiguration: EditorManagerConfiguration = this.buildTextEditorConfiguration();
        const documentTextDirection = this.defineTextDirection(this.language);
        this.defaultTextAlign = (documentTextDirection == 'ltr') ? TextAlignList.default : TextAlignList.list[2];
        this.initialContentConfiguration = {
            documentTextDirection: documentTextDirection,
            paragraphAlign: this.defaultTextAlign,
            documentFontFamily: this.defaultFontFamily.toLowerCase(),
            documentFontSize: this.defaultFontSize,
            paragraphFontWeight: [this.defaultFontWeight],
            paragraphFontStyle: [this.defaultFontStyle],
            paragraphTextDecoration: [this.defaultTextDecoration],
        };
        this.editorManager = new EditorManager(textEditorConfiguration, this.initialContentConfiguration);
        this.currentContentConfiguration = Object.assign({}, this.initialContentConfiguration);
        this.domEditorNode = this.elementRef.nativeElement.querySelector('div:last-child');
        this.editorManager.init(this.domEditorNode);
        this.menuManager = new MenuManager(this.editorManager, this.buildSuggestionRequestSubscriber(), this.buildCommandSubscriber());
        this.initShortcutsLabel();


        this.onFontFamilyChange = this.onFontFamilyChange.bind(this);
        document.addEventListener("fontFamilyChange", this.onFontFamilyChange);
    }

    ngOnDestroy(): void {
        this.editorManager.close();

        if (!!this.speechServiceSubscription) {
            this.speechServiceSubscription.unsubscribe();
        }
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        for (const propName of Object.keys(changes)) {
            if (propName === 'language' && this.editorManager) {
                const direction = this.defineTextDirection(changes[propName].currentValue);
                this.editorManager.updateDocumentAttribute({ name: "direction", value: direction });
            }
        }
    }

    public initShortcutsLabel() {
        this.translate.get(this.menuFontWeightConfiguration[0].label).subscribe((text: string) => { this.menuFontWeightConfiguration[0].label = text + ' ' + this.getShortcut('bold') });
        this.translate.get(this.menuAlignConfiguration[0].label).subscribe((text: string) => { this.menuAlignConfiguration[0].label = text + ' ' + this.getShortcut('align-left') });
        this.translate.get(this.menuAlignConfiguration[1].label).subscribe((text: string) => { this.menuAlignConfiguration[1].label = text + ' ' + this.getShortcut('align-center') });
        this.translate.get(this.menuAlignConfiguration[2].label).subscribe((text: string) => { this.menuAlignConfiguration[2].label = text + ' ' + this.getShortcut('align-right') });
        this.translate.get(this.menuAlignConfiguration[3].label).subscribe((text: string) => { this.menuAlignConfiguration[3].label = text + ' ' + this.getShortcut('align-justify') });
        this.translate.get(this.menuFontStyleConfiguration[0].label).subscribe((text: string) => { this.menuFontStyleConfiguration[0].label = text + ' ' + this.getShortcut('italic') });
        this.translate.get(this.menuTextDecorationConfiguration[0].label).subscribe((text: string) => { this.menuTextDecorationConfiguration[0].label = text + ' ' + this.getShortcut('underline') });
        this.translate.get(this.menuOrderedConfiguration[0].label).subscribe((text: string) => { this.menuOrderedConfiguration[0].label = text + ' ' + this.getShortcut('ordered-list') });
        this.translate.get(this.menuUnOrderedConfiguration[0].label).subscribe((text: string) => { this.menuUnOrderedConfiguration[0].label = text + ' ' + this.getShortcut('unordered-list') });
    }

    isMenuItemVisible(item: MenuEnum) {
        return this.isVisible &&
            (!this.menuConfig.has(item) || this.menuConfig.get(item)?.visible);
    }

    public getShortcut(action: string) {
        if (this.shortcutList.hasOwnProperty(action)) {
            const shortcut = this.shortcutList[action].replace("Mod-", "Ctrl+");
            return '(' + shortcut + ')';
        }
    }

    splitParagraph() {
        this.editorManager.splitParagraph();
    }

    public replaceCurrentParagraph(text: string, newLineAfter: boolean) {
        this.editorManager.replaceCurrentParagraph(text, newLineAfter);
    }

    public insertWithinCurrentParagraph(filter: string, text: string, newLineAfter: boolean) {
        this.editorManager.insertWithinCurrentParagraph(filter, text, newLineAfter);
    }

    public applyNormality() {
        const suggestion = this.suggestionList.filter(item => item.IsNormality);
        if (suggestion.length) {
            const normalText: Suggestion = {
                id: suggestion[0].id,
                Description: suggestion[0].code,
                Sentence: { 'text': suggestion[0].Sentence['text'] },
                IsNormality: suggestion[0].IsNormality,
                code: suggestion[0].code,
                newLineAfter: suggestion[0].newLineAfter
            };
            this.editorManager.clear();
            this.insertWithinCurrentParagraph('', this.getSentenceForTypeText(normalText), normalText.newLineAfter);
        }
        this.focus();
    }

    private getSentenceForTypeText(suggestion: Suggestion): string {
        if (!this.defaultTextName) {
            return '';
        }
        return suggestion.Sentence[this.defaultTextName];
    }

    public checkNormalityPresence() {
        return !!this.suggestionList.filter(item => item.IsNormality).length;
    }

    public focus(): void {
        this.editorManager.focus();
    }

    public clear(): void {
        this.currentContentConfiguration = Object.assign({}, this.initialContentConfiguration);
        this.editorManager.clear();
    }

    public appendText(text: string) {
        this.editorManager.appendText(text);
    }

    public replaceText(text: string) {
        this.editorManager.replaceText(text);
    }

    /* control value accessor implementation - reset the editor */
    public writeValue(editorContent: any): void {
        // this.logger.trace('text-editor writeValue: ' + editorContent);

        /* non emetto il prossimo evento in quanto il content change non e' determinato da un azione dell'utente */
        if (editorContent) {
            this.ignoreNextContentChange = true;
        }

        this.editorManager.reset();
        this.editorManager.replaceContent(editorContent, false);
    }

    /* replace the content withouth resetting the history*/
    public replaceContent(editorContent: any): void {
        this.editorManager.replaceContent(editorContent, true);
    }

    public registerOnChange(fn: any): void {
        this.changeListenerFn = fn;
    }

    public registerOnTouched(fn: any): void {
        this.touchListenerFn = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (this.editorManager) {
            this.editorManager.setDisabled(isDisabled);
        }
    }

    public setVisibility(isVisible: boolean) {
        this.isVisible = isVisible;

        if (!isVisible && !this.isDisabled) {
            this.clear();
        }

        this.visibilityNotify.emit(this.isVisible);
    }

    private buildCommandSubscriber() {
        return Subscriber.create<MenuEvent>((event: MenuEvent) => {
            this.commandRequested.emit(event);
        });
    }

    private setContentAttrs(conf) {
        // console.log('!!! setContentAttrs', conf);
        if (typeof conf !== 'undefined' && (Object.keys(conf).length !== 0)) {
            if (typeof conf.direction !== null) {
                this.currentContentConfiguration.documentTextDirection = conf.direction;
            }
            if (typeof conf.fontFamily !== null) {
                this.currentContentConfiguration.documentFontFamily = conf.fontFamily;
            }
            if (typeof conf.fontSize !== null) {
                this.currentContentConfiguration.documentFontSize = conf.fontSize;
            }
            if (typeof conf.textAlign !== null) {
                this.currentContentConfiguration.paragraphAlign = conf.textAlign;
            }
        }
    }

    private refreshTextFormatAttributes(attrs) {
        if (attrs) {
            for (const i in attrs) {
                if (attrs.hasOwnProperty(i)) {
                    const obj = attrs[i];
                    if (obj.name === TextFormats.bold) {
                        const value = (obj.value) ? [TextFormats.bold] : [''];
                        this.currentContentConfiguration.paragraphFontWeight = value;
                    }
                    if (obj.name === TextFormats.italic) {
                        const value = (obj.value) ? [TextFormats.italic] : [''];
                        this.currentContentConfiguration.paragraphFontStyle = value;
                    }
                    if (obj.name === TextFormats.underline) {
                        const value = (obj.value) ? [TextFormats.underline] : [''];
                        this.currentContentConfiguration.paragraphTextDecoration = value;
                    }
                }
            }
        }
    }

    private refreshListAttributes(isList) {
        this.listConfiguration.ordered = [''];
        this.listConfiguration.unordered = [''];
        if (isList) {
            this.listConfiguration[isList] = [isList];
        }
    }

    private onEditorSelectionChange = (event: SelectEvent) => {
        // console.log('!!! onEditorSelectionChange',  event); // this.lastSelectedEvent,
        if (this.lastSelectedEvent && event) {
            if ((
                this.lastSelectedEvent.startPos !== event.startPos ||
                this.lastSelectedEvent.endPos !== event.endPos ||
                this.lastSelectedEvent.text !== event.text)
            ) {

                // if (!!this.lastSelectedEvent.isList) {
                //     event.nodeAttrs = this.lastSelectedEvent.nodeAttrs;
                // }

                this.setContentAttrs(event.nodeAttrs);
                this.refreshTextFormatAttributes(event.textFormatAttributes);
                this.refreshListAttributes(event.isList);
            }
        }
        this.lastSelectedEvent = event;
        this.selectionChange.emit(event);
    }

    private onEditorContentChange = (event: ContentChangedEvent) => {
        this.isEmpty.emit(!event.text.trim());
        this.readOnlyContentDiv = event.innerHTML;

        if (!this.ignoreNextContentChange) {
            if (this.changeListenerFn) {
                this.changeListenerFn(event.innerHTML);
            }
            this.contentChange.emit(event);
        } else {
            this.ignoreNextContentChange = false;
        }
    }

    private onEditorToched = (touched: boolean) => {
        if (this.touchListenerFn) {
            this.touchListenerFn(touched);
        }
    }

    public setTextFormatAttributes(attr: string, style: TextFormats) {
        let apply = false;
        if (this.currentContentConfiguration[attr][0] === '') {
            this.currentContentConfiguration[attr] = [style];
            apply = true;
        } else {
            this.currentContentConfiguration[attr] = [''];
        }
        this.editorManager.setSelectedTextStyle(style, apply);
        this.editorManager.focus();
    }

    public setParagraphAttributes(attr: string, value: string) {
        this.currentContentConfiguration[attr] = value;
        this.editorManager.updateParagraphAttribute(this.currentContentConfiguration);
    }

    private onShortcutDetected = (event: ShortcutEvent) => {
        this.shortcutDetected.emit(event);
    }

    private buildShortcutList(): string[] {
        const shortcutList: string[] = [];
        for (let i = 1; i <= 12; i++) {
            shortcutList.push('F' + i);
        }
        for (const action in this.shortcutList) {
            if (this.shortcutList.hasOwnProperty(action)) {
                const shortcut = this.shortcutList[action];
                shortcutList.push(shortcut);
            }
        }
        return shortcutList;
    }

    private buildSuggestionRequestSubscriber() {
        return Subscriber.create<boolean>(() => {
            this.requestSuggestions.emit(true);
        });
    }

    private defineTextDirection(language: string): string {
        let result = 'ltr';
        if (language === 'he') {
            result = 'rtl';
        }
        return result;
    }

    private buildTextEditorConfiguration(): EditorManagerConfiguration {
        return {
            editorContentTextChangedSubscriber: Subscriber.create<ContentChangedEvent>(this.onEditorContentChange),
            selectionEventSubscriber: Subscriber.create<SelectEvent>(this.onEditorSelectionChange),
            editorTouchedSubscriber: Subscriber.create<boolean>(this.onEditorToched),
            shortcutEventSubscriber: Subscriber.create<ShortcutEvent>(this.onShortcutDetected),
            shortcutList: this.buildShortcutList()
        };
    }

    public onFontFamilyChange(fontFamily) {
        this.editorManager.updateDocumentAttribute({ name: "fontFamily", value: fontFamily });
        this.currentContentConfiguration.documentFontFamily = fontFamily;
        this.editorManager.focus();


        //test
    }

    public onFontSizeChange(fontSize) {
        this.editorManager.updateDocumentAttribute({ name: "fontSize", value: fontSize });
        this.currentContentConfiguration.documentFontSize = fontSize;
        this.editorManager.focus();
    }

    public onTextAlignChange = (value) => {
        this.currentContentConfiguration.paragraphAlign = value;
        this.editorManager.updateParagraphAttribute(this.currentContentConfiguration);
    }

    public onFontWeightChange = (value) => {
        let boldSelected = false;
        let style = '';
        if (value.length && value[0] === TextFormats.bold) {
            boldSelected = true;
            style = TextFormats.bold;
        }
        this.currentContentConfiguration.paragraphFontWeight = [style];
        this.editorManager.setSelectedTextStyle(TextFormats.bold, boldSelected);
        this.editorManager.focus();
    }

    public onFontStyleChange = (value) => {
        let italicSelected = false;
        let style = '';
        if (value.length && value[0] === TextFormats.italic) {
            italicSelected = true;
            style = TextFormats.italic;
        }
        this.currentContentConfiguration.paragraphFontStyle = [style];
        this.editorManager.setSelectedTextStyle(TextFormats.italic, italicSelected);
        this.editorManager.focus();
    }

    public onTextDecorationChange = (value) => {
        let underlineSelected = false;
        let style = '';
        if (value.length && value[0] === TextFormats.underline) {
            underlineSelected = true;
            style = TextFormats.underline;
        }
        this.currentContentConfiguration.paragraphTextDecoration = [style];
        this.editorManager.setSelectedTextStyle(TextFormats.underline, underlineSelected);
        this.editorManager.focus();
    }

    public onListClick = (value) => {
        let listType = this.editorManager.getListType();
        if (value.length) {
            switch (value[0]) {
                case ListFormats.unordered:
                    if (listType === ListFormats.unordered) {
                        this.editorManager.unsetListItem();
                    } else {
                        this.editorManager.setUnorderedListItem();
                    }
                    break;
                case ListFormats.ordered:
                    if (listType === ListFormats.ordered) {
                        this.editorManager.unsetListItem();
                    } else {
                        this.editorManager.setOrderedListItem();
                    }
                    break;
            }
        } else {
            this.editorManager.unsetListItem();
        }
        this.editorManager.focus();
    }

    public openSpeechModal() {
        let isTextEmpty = !this.readOnlyContentDiv || this.readOnlyContentDiv?.length === 0;
        const speechText = isTextEmpty ? '' : this.readOnlyContentDiv;

        this.startSpeechReconition.emit([
            speechText,
            this.titleCasePipe.transform(this.currentContentConfiguration.documentFontFamily),
            this.currentContentConfiguration.documentFontSize
        ]);
    }

}
